<template>
  <v-container fluid class="setup-page pa-6">

    <!-- Page Header -->
    <div class="setup-header mb-6">
      <div>
        <h2 class="setup-title">{{ selected_supplier.supplier_name }}</h2>
        <span class="setup-subtitle grey--text text--darken-1">Update supplier details</span>
      </div>
      <v-btn color="primary" rounded elevation="0" class="save-btn" :loading="saving" @click="updateSupplier()">
        <v-icon left small>mdi-content-save-outline</v-icon>
        Save Changes
      </v-btn>
    </div>

    <!-- Tabs -->
    <v-tabs v-model="tab" background-color="transparent" color="primary" class="setup-tabs mb-5">
      <v-tab v-for="section in sections" :key="section.key" class="tab-item">
        <v-icon left small>{{ section.icon }}</v-icon>
        {{ section.label }}
        <v-chip
          v-if="section.key === 'documents'"
          x-small label
          class="tab-count-chip ml-2"
          :color="documents.length > 0 ? 'primary' : '#E0E0E0'"
          :text-color="documents.length > 0 ? 'white' : '#9E9E9E'"
        >
          {{ documents.length }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="tabs-items">

      <!-- ── 1. Basic Info ── -->
      <v-tab-item>
        <v-row>
          <!-- Left column -->
          <v-col cols="12" md="8">
            <v-card class="section-card mb-4" elevation="0">
              <div class="section-header">
                <v-icon small class="mr-2" color="#525A68">mdi-information-outline</v-icon>
                General
              </div>
              <v-card-text class="pt-4">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.supplier_name" label="Supplier Name" outlined dense
                      hide-details="auto" prepend-inner-icon="mdi-store-outline" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-autocomplete v-model="selected_supplier.service_type_ids" :items="get_supplier_type" multiple
                      item-text="name" item-value="id" label="Supplier Types *" outlined dense
                      hide-details="auto" prepend-inner-icon="mdi-view-list-outline" chips small-chips
                      deletable-chips :error="type_error" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card class="section-card mb-4" elevation="0">
              <div class="section-header">
                <v-icon small class="mr-2" color="#525A68">mdi-account-outline</v-icon>
                Contact Details
              </div>
              <v-card-text class="pt-4">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.email" label="Booking Email" outlined dense
                      hide-details="auto" prepend-inner-icon="mdi-at" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.contact_name" label="Contact Name" outlined dense
                      hide-details="auto" prepend-inner-icon="mdi-account-outline" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.phone" label="Phone" outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-phone-outline" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.mobile" label="Mobile" outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-cellphone" />
                  </v-col>
                  <!-- <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="selected_supplier.free_phone"
                      label="Freephone"
                      outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-phone-outline"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="selected_supplier.others"
                      label="Other Number"
                      outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-phone-plus-outline"
                    />
                  </v-col> -->
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.website" label="Website" outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-web" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.contact_by" label="Preferred Contact Method" outlined dense
                      hide-details="auto" prepend-inner-icon="mdi-message-outline" placeholder="e.g. Email, Phone" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card class="section-card mb-4" elevation="0">
              <div class="section-header">
                <v-icon small class="mr-2" color="#525A68">mdi-map-marker-outline</v-icon>
                Location
              </div>
              <v-card-text class="pt-4">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-autocomplete v-model="supplier_locations" :items="getLocations" multiple item-text="name"
                      item-value="id" return-object label="Locations" outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-map-marker-multiple-outline" chips small-chips deletable-chips />
                  </v-col>
                  <!-- <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="selected_supplier.location_name"
                      label="Location Name / Area"
                      outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-map-marker-outline"
                    />
                  </v-col> -->
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.address" label="Full Address" outlined dense
                      hide-details="auto" prepend-inner-icon="mdi-home-outline" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.time_to_center" label="Time to City Center" outlined dense
                      hide-details="auto" prepend-inner-icon="mdi-clock-outline" placeholder="e.g. 10 mins" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="selected_supplier.time_to_arrive" label="Time to Arrive Early" outlined dense
                      hide-details="auto" prepend-inner-icon="mdi-clock-check-outline"
                      placeholder="e.g. 15 mins before" />
                  </v-col>
                  <v-col cols="12">
                    <v-textarea v-model="selected_supplier.directions" label="Directions" outlined dense
                      hide-details="auto" rows="3" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right column: Image -->
          <v-col cols="12" md="4">
            <v-card class="section-card mb-4" elevation="0">
              <div class="section-header">
                <v-icon small class="mr-2" color="#525A68">mdi-image-outline</v-icon>
                Supplier Image
              </div>
              <v-card-text class="pt-4 text-center">
                <div class="image-preview mb-4">
                  <img v-if="selected_supplier.image"
                    :src="api_url + 'media/suppliers/images/' + selected_supplier.image" class="supplier-preview-img" />
                  <img v-else-if="selected_supplier.sup_logo"
                    :src="api_url + 'media/suppliers/images/' + selected_supplier.sup_logo"
                    class="supplier-preview-img" />
                  <div v-else class="image-placeholder">
                    <v-icon size="56" color="#C8C8C8">mdi-image-off-outline</v-icon>
                    <div class="grey--text text--lighten-1 mt-2 caption">No image</div>
                  </div>
                </div>
                <v-file-input v-model="file" label="Upload Image" outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-upload" accept="image/*" @change="uploadImage()" />
              </v-card-text>
            </v-card>

            <v-card class="section-card" elevation="0">
              <div class="section-header">
                <v-icon small class="mr-2" color="#525A68">mdi-text-long</v-icon>
                Description
              </div>
              <v-card-text class="pt-4">
                <label class="field-label">Short Description</label>
                <v-textarea v-model="selected_supplier.short_description" label="Short Description" outlined dense
                  hide-details="auto" rows="1" />
              </v-card-text>
              <v-card-text class="pt-4">
                <label class="field-label">Full Description</label>
                <wysiwyg v-model="selected_supplier.description" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- ── 2. Policies ── -->
      <v-tab-item>
        <v-card class="section-card mb-4" elevation="0">
          <div class="section-header">
            <v-icon small class="mr-2" color="#525A68">mdi-file-document-outline</v-icon>
            Rules & Policies
          </div>
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="selected_supplier.currency" label="Currency" outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-currency-usd" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="selected_supplier.pay_type" label="Payment Type" outlined dense
                  hide-details="auto" prepend-inner-icon="mdi-credit-card-outline" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="selected_supplier.dress" label="Dress Code" outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-tshirt-crew-outline" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="selected_supplier.entrance" label="Entrance / Access" outlined dense
                  hide-details="auto" prepend-inner-icon="mdi-door-open" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="selected_supplier.age_limit" label="Age Limit" outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-account-alert-outline" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="selected_supplier.server_time_limit" label="Service Time Limit" outlined dense
                  hide-details="auto" prepend-inner-icon="mdi-timer-outline" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="selected_supplier.deposit_info" label="Deposit Info" outlined dense
                  hide-details="auto" prepend-inner-icon="mdi-bank-outline" />
              </v-col>
              <v-col cols="12" sm="3">
                <v-switch v-model="selected_supplier.has_breakfast" label="Has Breakfast" color="primary" hide-details
                  dense />
              </v-col>
              <v-col cols="12" sm="3">
                <v-switch v-model="selected_supplier.has_bar" label="Has Bar" color="primary" hide-details dense />
              </v-col>
              <v-col cols="12">
                <label class="field-label">Drinking Policy</label>
                <wysiwyg v-model="selected_supplier.drink_policy" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ── 3. Notes ── -->
      <v-tab-item>
        <v-row>
          <v-col cols="12" md="6">
            <v-card class="section-card mb-4" elevation="0">
              <div class="section-header">
                <v-icon small class="mr-2" color="#525A68">mdi-tooltip-text-outline</v-icon>
                General Notes
              </div>
              <v-card-text class="pt-4">
                <v-text-field v-model="selected_supplier.notes" label="Notes" outlined dense hide-details="auto"
                  class="mb-4" />
                <label class="field-label">Room Types</label>
                <v-text-field v-model="selected_supplier.room_types" label="Room Types" outlined dense
                  hide-details="auto" class="mb-4" />
                <label class="field-label">Other Offers</label>
                <wysiwyg v-model="selected_supplier.other_offers" class="mb-4" />
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card class="section-card mb-4" elevation="0">
              <div class="section-header">
                <v-icon small class="mr-2" color="#AA3939">mdi-shield-lock-outline</v-icon>
                Internal / Admin
              </div>
              <v-card-text class="pt-4">
                <label class="field-label">Admin Notes</label>
                <wysiwyg v-model="selected_supplier.admin_notes" class="mb-4" />
                <label class="field-label">Special Notes</label>
                <wysiwyg v-model="selected_supplier.special_notes" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- ── 4. Documents ── -->
      <v-tab-item>
        <v-card class="section-card mb-4" elevation="0">
          <div class="section-header">
            <v-icon small class="mr-2" color="#525A68">mdi-paperclip</v-icon>
            Documents
          </div>
          <v-card-text class="pt-4">
            <!-- Upload row -->
            <v-row align="center" class="mb-4">
              <v-col cols="12" sm="5">
                <v-file-input
                  v-model="doc_file"
                  label="Select file"
                  outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-upload"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="doc_name"
                  label="Document name (optional)"
                  outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-tag-outline"
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-btn
                  color="primary" rounded elevation="0"
                  :loading="doc_uploading"
                  :disabled="!doc_file"
                  @click="uploadDocument()"
                >
                  <v-icon left small>mdi-upload</v-icon>
                  Upload
                </v-btn>
              </v-col>
            </v-row>

            <!-- Empty state -->
            <div v-if="documents.length === 0" class="doc-empty-state">
              <v-icon size="52" color="#E0E0E0" class="mb-2">mdi-file-outline</v-icon>
              <div class="grey--text text--darken-1 body-2">No documents uploaded yet</div>
            </div>

            <!-- Document cards grid -->
            <v-row v-else>
              <v-col
                v-for="doc in documents"
                :key="doc.id"
                cols="12" sm="6" md="3"
              >
                <v-card class="doc-card" elevation="0">
                  <!-- Thumbnail -->
                  <a :href="api_url + 'media/suppliers/documents/' + doc.file_name" target="_blank" class="doc-thumb-link">
                    <div class="doc-thumb">
                      <img
                        v-if="doc.mime_type && doc.mime_type.startsWith('image/')"
                        :src="api_url + 'media/suppliers/documents/' + doc.file_name"
                        class="doc-img"
                      />
                      <div v-else class="doc-icon-wrap">
                        <v-icon size="48" :color="docIconColor(doc.mime_type)">{{ docIcon(doc.mime_type) }}</v-icon>
                      </div>
                    </div>
                  </a>

                  <v-card-text class="doc-body">
                    <div class="doc-name-row">
                      <div class="doc-name" :title="doc.name">{{ doc.name }}</div>
                      <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop>
                            <v-icon size="18" color="#9E9E9E">mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item :href="api_url + 'media/suppliers/documents/' + doc.file_name" target="_blank">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>View</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="openRenameDialog(doc)">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>Rename</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="openDeleteDialog(doc)">
                            <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                            <v-list-item-title class="error--text">Delete</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                    <div class="doc-date grey--text">{{ doc.created_at ? doc.created_at.substring(0, 10) : '' }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

    </v-tabs-items>

    <!-- Rename dialog -->
    <v-dialog v-model="rename_dialog" max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Rename Document</v-card-title>
        <v-card-text>
          <v-text-field v-model="rename_value" label="Document name" outlined dense autofocus />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rename_dialog = false">Cancel</v-btn>
          <v-btn color="primary" elevation="0" rounded @click="confirmRename()">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm dialog -->
    <v-dialog v-model="delete_dialog" max-width="380">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Delete Document</v-card-title>
        <v-card-text>Are you sure you want to delete <strong>{{ delete_doc && delete_doc.name }}</strong>? This cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="delete_dialog = false">Cancel</v-btn>
          <v-btn color="error" elevation="0" rounded @click="confirmDelete()">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Sticky Save Bar -->
    <div class="save-bar mt-6">
      <v-btn color="primary" rounded elevation="0" class="save-btn" :loading="saving" @click="updateSupplier()">
        <v-icon left small>mdi-content-save-outline</v-icon>
        Save Changes
      </v-btn>
    </div>

  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { lookups, loadLookups } from '@/common/companyLookups'
export default {
  components: {},
  props: ['supplier'],
  data: () => ({
    tab: 0,
    file: null,
    saving: false,
    type_error: false,
    supplier_locations: [],
    selected_supplier: {},
    api_url: null,
    sections: [
      { key: 'basic', label: 'Overview', icon: 'mdi-information-outline' },
      { key: 'policies', label: 'Policies', icon: 'mdi-file-document-outline' },
      { key: 'notes', label: 'Notes', icon: 'mdi-tooltip-text-outline' },
      { key: 'documents', label: 'Documents', icon: 'mdi-paperclip' },
    ],
    documents: [],
    doc_file: null,
    doc_name: '',
    doc_uploading: false,
    rename_dialog: false,
    rename_doc: null,
    rename_value: '',
    delete_dialog: false,
    delete_doc: null,
  }),
  async mounted() {
    this.api_url = process.env.VUE_APP_API_URL
    loadLookups('locations')
    await this.$store.dispatch('supplier/fetch_supplier_type')
    this.selected_supplier = { ...this.supplier }
    this.initSupplierLocations()
    await this.fetchDocuments()
  },
  computed: {
    getLocations() {
      return lookups.locations
    },
    ...mapGetters({
      get_supplier_type: 'supplier/get_supplier_type',
    }),
  },
  methods: {
    showSnackBar(message) {
      this.$store.commit('auth/setMessage', { show: true, message: message }, { root: 1 })
    },
    initSupplierLocations() {
      this.supplier_locations = (this.supplier.supplier_locations || []).map(sl => sl.location)
    },
    async uploadImage() {
      if (!this.file) return
      const formData = new FormData()
      formData.append('supplier_image', this.file)
      const { data } = await this.$axios.post(`admin/suppliers/upload_supplier_image/${this.selected_supplier.id}`, formData)
      if (data.response) {
        this.$toast.success(data.message)
        this.file = null
        this.selected_supplier.image = data.data?.image ?? this.selected_supplier.image
      }
    },
    async updateSupplier() {
      this.type_error = !this.selected_supplier.service_type_ids || this.selected_supplier.service_type_ids.length === 0
      if (this.type_error) {
        this.$toast.error('Please select at least one supplier type')
        return
      }
      this.saving = true
      const updated_supplier_locations = this.supplier_locations.map(l => l.id)
      const payload = {
        ...this.selected_supplier,
        updated_supplier_locations,
      }
      const { data } = await this.$axios.post(`admin/suppliers/update_supplier/${this.selected_supplier.id}`, payload)
      if (data.response) {
        this.showSnackBar(data.message)
        this.$emit('updated')
      }
      this.saving = false
    },

    async fetchDocuments() {
      const { data } = await this.$axios.get(`admin/suppliers/get_supplier_documents/${this.selected_supplier.id}`)
      if (data.response) {
        this.documents = data.data
      }
    },

    async uploadDocument() {
      if (!this.doc_file) return
      this.doc_uploading = true
      const formData = new FormData()
      formData.append('document', this.doc_file)
      formData.append('name', this.doc_name || this.doc_file.name)
      const { data } = await this.$axios.post(`admin/suppliers/upload_supplier_document/${this.selected_supplier.id}`, formData)
      if (data.response) {
        this.$toast.success(data.message)
        this.doc_file = null
        this.doc_name = ''
        this.documents.push(data.data)
      }
      this.doc_uploading = false
    },

    openRenameDialog(doc) {
      this.rename_doc = doc
      this.rename_value = doc.name
      this.rename_dialog = true
    },

    async confirmRename() {
      if (!this.rename_doc || !this.rename_value.trim()) return
      const { data } = await this.$axios.post(`admin/suppliers/rename_supplier_document/${this.rename_doc.id}`, { name: this.rename_value })
      if (data.response) {
        this.$toast.success(data.message)
        const idx = this.documents.findIndex(d => d.id === this.rename_doc.id)
        if (idx !== -1) this.documents[idx].name = this.rename_value
      }
      this.rename_dialog = false
      this.rename_doc = null
    },

    openDeleteDialog(doc) {
      this.delete_doc = doc
      this.delete_dialog = true
    },

    async confirmDelete() {
      if (!this.delete_doc) return
      const { data } = await this.$axios.delete(`admin/suppliers/delete_supplier_document/${this.delete_doc.id}`)
      if (data.response) {
        this.$toast.success(data.message)
        this.documents = this.documents.filter(d => d.id !== this.delete_doc.id)
      }
      this.delete_dialog = false
      this.delete_doc = null
    },

    docIcon(mime) {
      if (!mime) return 'mdi-file-outline'
      if (mime === 'application/pdf') return 'mdi-file-pdf-box'
      if (mime.includes('word') || mime.includes('docx') || mime.includes('doc')) return 'mdi-file-word-box'
      if (mime.includes('excel') || mime.includes('xlsx') || mime.includes('xls') || mime.includes('spreadsheet')) return 'mdi-file-excel-box'
      if (mime.includes('powerpoint') || mime.includes('pptx') || mime.includes('ppt') || mime.includes('presentation')) return 'mdi-file-powerpoint-box'
      if (mime.startsWith('text/')) return 'mdi-file-document-outline'
      if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return 'mdi-folder-zip-outline'
      return 'mdi-file-outline'
    },

    docIconColor(mime) {
      if (!mime) return '#9E9E9E'
      if (mime === 'application/pdf') return '#E53935'
      if (mime.includes('word') || mime.includes('docx') || mime.includes('doc')) return '#1565C0'
      if (mime.includes('excel') || mime.includes('xlsx') || mime.includes('xls') || mime.includes('spreadsheet')) return '#2E7D32'
      if (mime.includes('powerpoint') || mime.includes('pptx') || mime.includes('ppt') || mime.includes('presentation')) return '#E65100'
      if (mime.startsWith('text/')) return '#546E7A'
      if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return '#6D4C41'
      return '#9E9E9E'
    },
  },
}
</script>

<style scoped lang="scss">
.setup-page {
  background-color: #F5F6FA;
  min-height: 100vh;
}

/* Header */
.setup-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  .setup-title {
    font-size: 20px;
    font-weight: 700;
    color: #2C2C2C;
    margin: 0;
    line-height: 1.2;
  }

  .setup-subtitle {
    font-size: 13px;
    display: block;
    margin-top: 2px;
  }
}

/* Tabs */
.setup-tabs {
  border-bottom: 2px solid #EBEBEB;

  .tab-item {
    text-transform: none;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.2px;
    min-width: 110px;
  }
}

.tab-count-chip {
  font-size: 10px !important;
  font-weight: 700;
  border-radius: 5px !important;
  height: 18px !important;
}

:deep(.v-tabs-slider-wrapper) {
  height: 2px !important;
}

.tabs-items {
  background: transparent !important;
}

/* Section Cards */
.section-card {
  border-radius: 12px !important;
  border: 1px solid #E8E8E8 !important;
  background-color: #fff;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #7C7C7C;
  background-color: #FAFAFA;
  border-bottom: 1px solid #F0F0F0;
  padding: 10px 16px;
}

/* Field label */
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #555;
  margin-bottom: 6px;
}

/* Image preview */
.image-preview {
  width: 100%;
  height: 160px;
  border-radius: 10px;
  overflow: hidden;
  background-color: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.supplier-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Save bar */
.save-bar {
  display: flex;
  justify-content: flex-end;
}

/* Document empty state */
.doc-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  text-align: center;
}

/* Document card */
.doc-card {
  border-radius: 12px !important;
  border: 1px solid #E8E8E8 !important;
  background-color: #fff;
  overflow: hidden;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.09) !important;
    transform: translateY(-1px);
  }
}

.doc-thumb-link {
  display: block;
  text-decoration: none;

  &:hover .doc-thumb {
    opacity: 0.88;
  }
}

.doc-thumb {
  position: relative;
  width: 100%;
  height: 140px;
  background-color: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.doc-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.doc-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.doc-body {
  padding: 12px 14px 10px !important;
}

.doc-name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}

.doc-name {
  font-size: 13px;
  font-weight: 700;
  color: #2C2C2C;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.doc-date {
  font-size: 11px;
  margin-top: 2px;
}

.save-btn {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 0 24px;
  height: 38px;
}
</style>
